import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Shield, Loader2, ArrowRight, ArrowLeft, CheckCircle, HelpCircle, AlertTriangle, Save, Cloud, LayoutDashboard } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import axios from 'axios';
import API_URL from '../lib/apiUrl';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useAuth } from '../contexts/AuthContext';
import { useAutoSave } from '../hooks/useAutoSave';


// Custom styled select item for colored options
const ColoredSelectItem = ({ value, color, children }) => {
  const colorClasses = {
    green: 'text-green-600',
    yellow: 'text-yellow-600',
    red: 'text-red-600',
    default: 'text-black'
  };
  
  return (
    <SelectItem value={value} className={colorClasses[color] || colorClasses.default}>
      {children}
    </SelectItem>
  );
};

// Display component for selected value with color
const SelectedValueDisplay = ({ value, options }) => {
  const option = options.find(opt => opt.value === value);
  if (!option) return <span className="text-slate-500">Select an option</span>;
  
  const colorClasses = {
    green: 'text-green-600 font-medium',
    yellow: 'text-yellow-600 font-medium',
    red: 'text-red-600 font-medium',
    default: 'text-black'
  };
  
  return <span className={colorClasses[option.color] || colorClasses.default}>{option.label}</span>;
};

const CoreAssessment = () => {
  const { orgId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  
  const [formData, setFormData] = useState({
    top_management_involvement: '',
    defined_security_objectives: '',
    risk_assessment_conducted: '',
    asset_inventory_maintained: '',
    hr_onboarding_security_checks: '',
    access_control_process: '',
    password_policy: '',
    cryptography_standards: '',
    incident_reporting_process: '',
    business_continuity_documented: '',
    supplier_assessment: '',
    change_management: '',
    acceptable_use_policy: '',
    staff_training: '',
    security_maintenance_frequency: ''
  });

  // Auto-save hook - saves every 60 seconds
  const { 
    isSaving, 
    lastSaved, 
    saveDraft, 
    deleteDraft,
    saveSuccess
  } = useAutoSave('core_assessment', orgId, formData, setFormData, token, 30000, !initialLoading, false);

  // Load existing data on mount
  useEffect(() => {
    const loadExistingData = async () => {
      try {
        const headers = { Authorization: `Bearer ${token}` };
        const [persistedResponse, draftResponse] = await Promise.all([
          axios.get(`${API_URL}/api/core-assessment/${orgId}`, { headers, params: { optional: true } }),
          axios.get(`${API_URL}/api/drafts/core_assessment`, { headers, params: { org_id: orgId } }).catch(() => ({ data: null }))
        ]);
        const persisted = persistedResponse.data || {};
        const draft = draftResponse.data?.draft?.data || {};
        setFormData((current) => ({ ...current, ...persisted, ...draft }));
      } catch (error) {
        // No existing data, that's fine
        console.log('No existing core assessment data');
      } finally {
        setInitialLoading(false);
      }
    };
    loadExistingData();
  }, [orgId, token]);

  // Track unsaved changes
  const updateFormData = useCallback((newData) => {
    setFormData(newData);
    setHasUnsavedChanges(true);
  }, []);

  // Browser tab close warning - always show on assessment pages
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      const message = 'Data you entered is not saved. Are you sure you want to close?';
      e.preventDefault();
      e.returnValue = message;
      return message;
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  // Question definitions
  const questions = [
    {
      id: 'top_management_involvement',
      number: 1,
      question: 'Top management involvement',
      options: [
        { value: 'high', label: 'High', color: 'green' },
        { value: 'medium', label: 'Medium', color: 'yellow' },
        { value: 'low', label: 'Low', color: 'red' }
      ]
    },
    {
      id: 'defined_security_objectives',
      number: 2,
      question: 'Defined security objectives?',
      options: [
        { value: 'yes', label: 'Yes', color: 'green' },
        { value: 'no', label: 'No', color: 'red' },
        { value: 'unaware', label: 'Unaware', color: 'yellow' }
      ]
    },
    {
      id: 'risk_assessment_conducted',
      number: 3,
      question: 'Risk assessment conducted?',
      options: [
        { value: 'yes', label: 'Yes', color: 'green' },
        { value: 'no', label: 'No', color: 'red' },
        { value: 'unaware', label: 'Unaware', color: 'yellow' }
      ]
    },
    {
      id: 'asset_inventory_maintained',
      number: 4,
      question: 'Asset inventory maintained?',
      options: [
        { value: 'yes', label: 'Yes', color: 'green' },
        { value: 'no', label: 'No', color: 'red' },
        { value: 'unaware', label: 'Unaware', color: 'yellow' }
      ]
    },
    {
      id: 'hr_onboarding_security_checks',
      number: 5,
      question: 'HR onboarding with security checks?',
      options: [
        { value: 'yes', label: 'Yes', color: 'green' },
        { value: 'no', label: 'No', color: 'red' },
        { value: 'unaware', label: 'Unaware', color: 'yellow' }
      ]
    },
    {
      id: 'access_control_process',
      number: 6,
      question: 'Access control process is followed in the organization?',
      options: [
        { value: 'yes', label: 'Yes', color: 'green' },
        { value: 'no', label: 'No', color: 'red' },
        { value: 'unaware', label: 'Unaware', color: 'yellow' }
      ]
    },
    {
      id: 'password_policy',
      number: 7,
      question: 'Password policy is created, maintained and updated regularly?',
      options: [
        { value: 'yes', label: 'Yes', color: 'green' },
        { value: 'no', label: 'No', color: 'red' },
        { value: 'unaware', label: 'Unaware', color: 'yellow' }
      ]
    },
    {
      id: 'cryptography_standards',
      number: 8,
      question: 'Cryptography standards implemented in the business process?',
      options: [
        { value: 'yes', label: 'Yes', color: 'green' },
        { value: 'no', label: 'No', color: 'red' },
        { value: 'unaware', label: 'Unaware', color: 'yellow' }
      ]
    },
    {
      id: 'incident_reporting_process',
      number: 9,
      question: 'Incident reporting process?',
      options: [
        { value: 'process_defined', label: 'Process defined', color: 'green' },
        { value: 'not_defined', label: 'Not defined', color: 'red' },
        { value: 'unaware', label: 'Unaware', color: 'yellow' }
      ]
    },
    {
      id: 'business_continuity_documented',
      number: 10,
      question: 'Business continuity documented?',
      options: [
        { value: 'yes', label: 'Yes', color: 'green' },
        { value: 'no', label: 'No', color: 'red' },
        { value: 'unaware', label: 'Unaware', color: 'yellow' }
      ]
    },
    {
      id: 'supplier_assessment',
      number: 11,
      question: 'Supplier assessment is followed in the organization?',
      options: [
        { value: 'yes', label: 'Yes', color: 'green' },
        { value: 'no', label: 'No', color: 'red' },
        { value: 'unaware', label: 'Unaware', color: 'yellow' }
      ]
    },
    {
      id: 'change_management',
      number: 12,
      question: 'Change management followed in the company and teams?',
      options: [
        { value: 'yes', label: 'Yes', color: 'green' },
        { value: 'no', label: 'No', color: 'red' },
        { value: 'unaware', label: 'Unaware', color: 'yellow' }
      ]
    },
    {
      id: 'acceptable_use_policy',
      number: 13,
      question: 'Acceptable use policy created, maintained and updated regularly?',
      options: [
        { value: 'yes', label: 'Yes', color: 'green' },
        { value: 'no', label: 'No', color: 'red' },
        { value: 'unaware', label: 'Unaware', color: 'yellow' }
      ]
    },
    {
      id: 'staff_training',
      number: 14,
      question: 'Staff training about compliance and ISMS conducted regularly?',
      options: [
        { value: 'yes', label: 'Yes', color: 'green' },
        { value: 'no', label: 'No', color: 'red' },
        { value: 'unaware', label: 'Unaware', color: 'yellow' }
      ]
    },
    {
      id: 'security_maintenance_frequency',
      number: 15,
      question: 'How often overall information security maintenance conducted?',
      options: [
        { value: 'yearly', label: 'Yearly', color: 'green' },
        { value: 'half_yearly', label: 'Half Yearly', color: 'green' },
        { value: 'unaware', label: 'Unaware', color: 'yellow' }
      ]
    }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate all questions are answered
    for (const question of questions) {
      if (!formData[question.id]) {
        toast.error(`Please answer question ${question.number}: "${question.question}"`);
        return;
      }
    }
    
    setLoading(true);

    try {
      // Save core assessment data
      await axios.post(
        `${API_URL}/api/core-assessment`,
        { 
          org_id: orgId,
          ...formData 
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setHasUnsavedChanges(false);
      await deleteDraft(); // Clean up draft after successful save
      toast.success('Core assessment saved!');
      
      // Navigate to Technical Controls page
      navigate(`/technical-controls/${orgId}`);
    } catch (error) {
      toast.error('Failed to save core assessment');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate(`/organization?orgId=${orgId}`);
  };

  // Get icon based on selected value color
  const getStatusIcon = (questionId) => {
    const value = formData[questionId];
    if (!value) return null;
    
    const question = questions.find(q => q.id === questionId);
    const option = question?.options.find(opt => opt.value === value);
    
    if (!option) return null;
    
    switch (option.color) {
      case 'green':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'yellow':
        return <HelpCircle className="h-5 w-5 text-yellow-500" />;
      case 'red':
        return <AlertTriangle className="h-5 w-5 text-red-500" />;
      default:
        return null;
    }
  };

  if (initialLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-slate-900 mx-auto mb-4" />
          <p className="font-body text-lg text-slate-600">Loading assessment data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-slate-50 py-12">
      <div className="max-w-3xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Back to Dashboard Button */}
          <div className="mb-6">
            <Button 
              variant="outline" 
              onClick={() => navigate('/dashboard')}
              className="gap-2"
            >
              <LayoutDashboard className="h-4 w-4" />
              Back to Dashboard
            </Button>
          </div>

          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-3">
                <Shield className="h-8 w-8 text-slate-900" />
                <h1 className="font-heading font-bold text-4xl text-black">
                  ISO 27001 Standard - CORE
                </h1>
              </div>
              <p className="font-body text-lg text-slate-700">
                Answer the following questions about your organization&apos;s information security practices
              </p>
            </div>

            {/* Progress Indicator */}
            <div className="mb-8">
            <div className="flex items-center justify-between mb-2">
              <span className="font-mono text-xs uppercase tracking-widest text-slate-600">Step 2 of 3</span>
              <span className="font-mono text-xs text-slate-600">Core Assessment</span>
            </div>
            <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
              <div className="h-full bg-slate-900 w-2/3 progress-bar"></div>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-lg p-8 space-y-6">
            
            {/* Questions */}
            {questions.map((question) => (
              <div key={question.id} className="pb-4 border-b border-slate-100 last:border-b-0">
                <Label className="font-body text-sm font-medium text-black mb-2 block">
                  {question.number}. {question.question} <span className="text-red-500">*</span>
                </Label>
                <div className="flex items-center gap-3">
                  <Select
                    value={formData[question.id]}
                    onValueChange={(value) => updateFormData({ ...formData, [question.id]: value })}
                  >
                    <SelectTrigger data-testid={`${question.id}-select`} className="text-black flex-1">
                      {formData[question.id] ? (
                        <SelectedValueDisplay value={formData[question.id]} options={question.options} />
                      ) : (
                        <span className="text-slate-500">Select an option</span>
                      )}
                    </SelectTrigger>
                    <SelectContent>
                      {question.options.map((option) => (
                        <ColoredSelectItem key={option.value} value={option.value} color={option.color}>
                          {option.label}
                        </ColoredSelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className="w-6">
                    {getStatusIcon(question.id)}
                  </div>
                </div>
              </div>
            ))}

            {/* Navigation Buttons */}
            <div className="pt-6 border-t border-slate-200">
              {/* Save Draft Button with Status */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2 text-sm">
                  {isSaving ? (
                    <span className="flex items-center gap-1 text-slate-500">
                      <Loader2 className="h-3 w-3 animate-spin" />
                      Saving...
                    </span>
                  ) : saveSuccess ? (
                    <span className="flex items-center gap-1 text-green-600 font-medium">
                      <Cloud className="h-4 w-4" />
                      Draft saved successfully
                    </span>
                  ) : lastSaved ? (
                    <span className="text-slate-500">
                      <Cloud className="h-4 w-4 inline mr-1" />
                      Last saved: {lastSaved.toLocaleTimeString()}
                    </span>
                  ) : null}
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={saveDraft}
                  disabled={isSaving}
                  className="gap-2"
                >
                  <Save className="h-4 w-4" />
                  Save Draft
                </Button>
              </div>
              
              <div className="flex gap-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleBack}
                  className="flex-1 py-6 text-base font-semibold"
                  data-testid="back-button"
                >
                  <ArrowLeft className="mr-2 h-5 w-5" />
                  Back
                </Button>
                <Button
                  type="submit"
                  className="flex-1 py-6 text-base font-semibold"
                  disabled={loading}
                  data-testid="next-button"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      Next
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </>
                  )}
                </Button>
              </div>
            </div>
          </form>
        </motion.div>
      </div>
    </div>
  );
};

export default CoreAssessment;
